import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';

/**
 * Hook de Favoritos personales del sidebar.
 *
 * Persiste un Set de paths "estrellados" por usuario en localStorage.
 * Cuando el usuario cambia, recarga los favoritos correspondientes.
 *
 * Storage key: `chirino-fav-sidebar:<email>` (o `:guest` si no hay user).
 */
export function useFavoritos() {
  const { user } = useAuth();
  const userKey = user?.email || 'guest';
  const storageKey = `chirino-fav-sidebar:${userKey}`;

  const [favoritos, setFavoritos] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });

  // Reload on user change
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      setFavoritos(new Set(raw ? JSON.parse(raw) : []));
    } catch {
      setFavoritos(new Set());
    }
  }, [storageKey]);

  // Permitir limpiar todos los favoritos vía Cmd+K
  useEffect(() => {
    const handler = () => {
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
      setFavoritos(new Set());
    };
    window.addEventListener('chirino-clear-favoritos', handler);
    return () => window.removeEventListener('chirino-clear-favoritos', handler);
  }, [storageKey]);

  const toggle = useCallback((path) => {
    setFavoritos((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      try {
        localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {/* ignore */}
      return next;
    });
  }, [storageKey]);

  const isFav = useCallback((path) => favoritos.has(path), [favoritos]);

  return { favoritos, isFav, toggle };
}
